import { makeStyles, tokens } from '@fluentui/react-components';

export const useStyles = makeStyles({
  // Tab row
  tabRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tabs: {
    borderBottom: 'none',
  },
  tabActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalXS,
    flexShrink: 0,
  },

  // Calendar view (LLM Queries tab)
  calendarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  calendarOuter: {
    overflowX: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  calendarInner: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: '200px',
  },
  calendarDayCol: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minWidth: '0',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': {
      borderRight: 'none',
    },
  },
  calendarDayHeader: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `2px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
  },
  calendarDayEntries: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalS,
    flex: '1',
  },
  calendarEntry: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  calendarGroup: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'hidden',
    ':hover': {
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  calendarGroupHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  calendarGroupHeaderExpanded: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  calendarGroupHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  calendarGroupCount: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  calendarGroupChevron: {
    width: '16px',
    height: '16px',
    minWidth: '16px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  calendarGroupSigs: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  calendarGroupSig: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  calendarGroupSigPrompt: {
    minWidth: 0,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  calendarGroupSigMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  calendarGroupSigId: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
  },
  calendarEntryTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  calendarEntryPrompt: {
    minWidth: 0,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  calendarEntryId: {
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  calendarEntryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  calendarEntryBadge: {
    fontSize: '10px',
    fontWeight: tokens.fontWeightSemibold,
    padding: '0px 3px',
    borderRadius: '3px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    lineHeight: '14px',
  },
  calendarEntryBadgeAugloop: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  calendarEntryBadgeSydney: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  calendarEntryBadgeCowork: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
    color: tokens.colorPaletteDarkOrangeForeground2,
  },
  calendarEntryApp: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  calendarEntryAppAugloop: {
    color: tokens.colorBrandForeground1,
  },
  calendarFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  calendarFilterPopover: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: '180px',
    padding: tokens.spacingHorizontalM,
  },
  calendarFilterLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: tokens.spacingVerticalXS,
  },

  // Recently Viewed tab
  recentEntries: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  recentEntry: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  recentEntryTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentEntryCreated: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentEntryCv: {
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentEntryPrompt: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flex: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    marginBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  recentHeaderTime: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentHeaderCreated: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentHeaderCv: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resizeHandle: {
    width: '8px',
    flexShrink: 0,
    cursor: 'col-resize',
    alignSelf: 'stretch',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      borderLeft: `2px solid ${tokens.colorBrandStroke1}`,
    },
  },
  colDivider: {
    width: '8px',
    flexShrink: 0,
  },
  recentHeaderPrompt: {
    flex: '1',
  },
  recentHeaderSortable: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  recentHeaderSortActive: {
    color: tokens.colorBrandForeground1,
  },

  // Search tab
  searchTab: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    maxWidth: '600px',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  searchInput: {
    flex: '1',
  },
  searchHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Load from JSON tab
  ocvTab: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
  },
  ocvTextarea: {
    width: '100%',
  },
  ocvTextareaInner: {
    height: '300px',
    resize: 'none',
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
  },
  ocvActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  ocvHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Empty / loading states
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
});
